import { Coffee, ShieldCheck, Ban, Info } from "lucide-react";

/**
 * Compact pre-shift briefing card that surfaces the effective status-check
 * and break rules for the guard's current site. Renders on the Officer Portal
 * once we have `siteCtx` from /portal/shift-context so guards know what to
 * expect before a break is denied or an unpaid deduction surprises them at
 * end-of-shift.
 *
 * Props:
 *   site: { name, status_check_interval_minutes, status_check_grace_minutes,
 *           breaks_enabled, paid_break_max_minutes, all_breaks_unpaid }
 */
export default function ShiftPolicyCard({ site }) {
  if (!site) return null;

  const scEnabled = !!site.status_check_interval_minutes && site.status_check_interval_minutes > 0;
  const breaksOff = site.breaks_enabled === false;
  const unpaidSite = !!site.all_breaks_unpaid;
  const paidMax = site.paid_break_max_minutes ?? 15;   // fallback matches server default

  return (
    <div
      className="border border-[#27272A] bg-[#0F0F0F] p-4"
      data-testid="shift-policy-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Info className="size-4 text-[#0A84FF]" />
        <div className="label-mute text-[#0A84FF]">Site Policy · {site.name || "current site"}</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Status-check row */}
        <div
          className="flex items-start gap-3 border border-[#1F1F1F] bg-[#121212] p-3"
          data-testid="policy-status-check"
        >
          <ShieldCheck
            className={`size-4 mt-0.5 flex-shrink-0 ${scEnabled ? "text-[#FF9F0A]" : "text-zinc-600"}`}
          />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Status Checks</div>
            {scEnabled ? (
              <div className="text-sm mt-0.5">
                Every <span className="font-mono font-bold text-[#FF9F0A]">{site.status_check_interval_minutes}min</span>
                <span className="text-zinc-500"> · {site.status_check_grace_minutes || 5}min grace</span>
              </div>
            ) : (
              <div className="text-sm mt-0.5 text-zinc-500">Not required at this site</div>
            )}
          </div>
        </div>

        {/* Break policy row */}
        <div
          className={`flex items-start gap-3 border p-3 ${
            breaksOff
              ? "border-[#FF3B30]/40 bg-[#FF3B30]/5"
              : unpaidSite
                ? "border-[#FF9F0A]/40 bg-[#FF9F0A]/5"
                : "border-[#1F1F1F] bg-[#121212]"
          }`}
          data-testid="policy-breaks"
        >
          {breaksOff ? (
            <Ban className="size-4 mt-0.5 text-[#FF3B30] flex-shrink-0" />
          ) : (
            <Coffee className={`size-4 mt-0.5 flex-shrink-0 ${unpaidSite ? "text-[#FF9F0A]" : "text-[#34C759]"}`} />
          )}
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Breaks</div>
            {breaksOff ? (
              <div className="text-sm mt-0.5 text-[#FF3B30]">Not allowed at this site</div>
            ) : unpaidSite ? (
              <div className="text-sm mt-0.5">
                <span className="text-[#FF9F0A] font-bold">Unpaid site</span>
                <span className="text-zinc-500"> · full duration deducted</span>
              </div>
            ) : (
              <div className="text-sm mt-0.5">
                Paid up to <span className="font-mono font-bold text-[#34C759]">{paidMax}min</span>
                <span className="text-zinc-500"> · longer = unpaid</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
